/**
 * A-3 定價與促銷（admin 權限；PRD §A-3 / AC-A3-*）。
 *
 * 商品下拉用的 lookup 在 `./products`（listProductsLite）。
 */

import { apiFetch, buildQueryString, type Schemas } from './core';

export const PRICING_PROMOTION_TYPES = [
  'discount_pct',
  'fixed_amount_off',
  'bonus',
  'package_bundle',
] as const;
export type PricingPromotionType = (typeof PRICING_PROMOTION_TYPES)[number];

export const PRICING_TICKET_TYPES = [
  'adult',
  'senior',
  // 業主價目表把兒童分成 3-5 與 6-11 兩段（見 migration 20260819050000）。
  // `child` 保留是因為既有訂單還在用它，新商品請用下面兩個。
  'child',
  'child_3_5',
  'child_6_11',
  'infant',
  'early_bird',
  'package_family',
  'custom',
] as const;
export type PricingTicketType = (typeof PRICING_TICKET_TYPES)[number];

export const PRICING_APPLICABLE_CHANNELS = [
  'web',
  'kiosk',
  'partner',
  'floor',
  'recovery',
] as const;
export type PricingApplicableChannel = (typeof PRICING_APPLICABLE_CHANNELS)[number];

export interface PricingBonusConfig {
  bonus_product_id: string;
  bonus_quantity: number;
}

export interface PricingPackageItem {
  product_id: string;
  quantity: number;
}

export interface PricingPackageConfig {
  items: PricingPackageItem[];
  bundle_price: number;
}

/**
 * 票價 / 優惠規則 row（後端 PricingRecord 之 CMS 子集；金額為 numeric → string，
 * 日期欄位以 ISO-8601 字串傳輸）。
 */
export type PricingRule = Schemas['PricingResponseDto'];

export interface CreatePricingDto {
  product_id: string;
  schedule_id?: string;
  ticket_type: PricingTicketType;
  promotion_type: PricingPromotionType;
  list_price: number;
  discount_pct?: number;
  fixed_amount_off?: number;
  bonus_config?: PricingBonusConfig;
  package_config?: PricingPackageConfig;
  display_name_i18n?: Record<string, string>;
  effective_from?: string;
  effective_to?: string;
  is_active?: boolean;
  weekday_mask?: number;
  min_remaining_tickets?: number;
  applicable_channels?: PricingApplicableChannel[];
  min_party_size?: number;
  max_party_size?: number;
}

/**
 * Patch 更新：product_id / ticket_type / promotion_type 三欄不可改（BE 約束）。
 */
export interface UpdatePricingDto {
  schedule_id?: string;
  list_price?: number;
  discount_pct?: number;
  fixed_amount_off?: number;
  bonus_config?: PricingBonusConfig;
  package_config?: PricingPackageConfig;
  display_name_i18n?: Record<string, string>;
  effective_from?: string;
  effective_to?: string;
  is_active?: boolean;
  weekday_mask?: number;
  min_remaining_tickets?: number;
  applicable_channels?: PricingApplicableChannel[];
  min_party_size?: number;
  max_party_size?: number;
}

export interface ListPricingQuery {
  product_id?: string;
  schedule_id?: string;
  promotion_type?: PricingPromotionType;
  ticket_type?: PricingTicketType;
  is_active?: boolean;
  applicable_channel?: PricingApplicableChannel;
  limit?: number;
  offset?: number;
  keyword?: string;
}

export interface ListPricingResult {
  items: PricingRule[];
  total: number;
  limit: number;
  offset: number;
}

export function listPricing(query: ListPricingQuery = {}): Promise<ListPricingResult> {
  const qs = buildQueryString({
    product_id: query.product_id,
    schedule_id: query.schedule_id,
    promotion_type: query.promotion_type,
    ticket_type: query.ticket_type,
    is_active: query.is_active,
    applicable_channel: query.applicable_channel,
    limit: query.limit,
    offset: query.offset,
    keyword: query.keyword,
  });
  return apiFetch<ListPricingResult>(`/pricing${qs}`, { method: 'GET' });
}

export function getPricing(id: string): Promise<PricingRule> {
  return apiFetch<PricingRule>(`/pricing/${encodeURIComponent(id)}`, { method: 'GET' });
}

export function createPricing(dto: CreatePricingDto): Promise<PricingRule> {
  return apiFetch<PricingRule>('/pricing', { method: 'POST', body: dto });
}

export function updatePricing(id: string, dto: UpdatePricingDto): Promise<PricingRule> {
  return apiFetch<PricingRule>(`/pricing/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: dto,
  });
}
